import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Dropdown, useDropdownMenu } from 'react-overlays';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import SimpleBar from './vendor/SimpleBar';
import IconArrowDown from '../public/images/arrow-down-mini.svg';
import {useCallback, useMemo} from "react";
import { sendEvent } from '../utils/analytics';
import { itemsOther } from './Footer';
import {getTranslation} from "../utils/getTranslation";

const LangBox = ({ className, ...props }) => {
  const { asPath, locale } = useRouter();

  if (itemsOther.some(({href}) => href === asPath && !href.includes('blog'))) {
    return null;
  }

  return (
    <Dropdown itemSelector="a.lang-box__link">
      <div className={classNames("lang-box", className)} {...props}>
        <Dropdown.Toggle>
          {(props) => (
            <button className="lang-box__button" type="button" {...props}>
              <span className="lang-box__button__text">
                  {locale.toUpperCase()}
              </span>
              <IconArrowDown className="lang-box__button__arrow" aria-hidden="true" />
            </button>
          )}
        </Dropdown.Toggle>
        <LangBoxDropdown/>
      </div>
    </Dropdown>
  );
};

const LangBoxDropdown = () => {
  const {asPath, locale: currentLocale, locales} = useRouter();

  const [props, {hasShown}] = useDropdownMenu({
    flip: true,
  });

  if (process.env.NODE_ENV === 'development' && !hasShown) {
    return null;
  }

  return (
    <div className="lang-box__dropdown" role="menu" {...props}>
      <SimpleBar>
        {locales.map((locale) => (
          <LangBoxItem
            key={locale}
            href={asPath}
            locale={locale}
            active={locale === currentLocale}
            currentLocale={currentLocale}
          />
        ))}
      </SimpleBar>
    </div>
  );
};

const LangBoxItem = ({ href, locale, active, currentLocale }) => {
  const { t } = useTranslation(['common', 'blog']);
  const handleClick = useCallback(() => {
    sendEvent({
      category: 'change-language',
      action: 'click',
      label: checkLangForAnalytics(locale),
    });
  }, [locale]);

  const newHref = useMemo(() => {
    const hrefReplace = href.replace('?#howto', '')
    if (hrefReplace.includes('/blog/')) {
      const translation = getTranslation(locale, 'articles')
      const currentTranslation = getTranslation(currentLocale, 'articles')
      const url = hrefReplace.replace("/blog/", "").replaceAll("%20", "");
      const englishTitle = currentTranslation[url] && currentTranslation[url].englishTitle;
      const [key] = translation && Object.entries(translation).find(([, article]) => article.englishTitle === englishTitle);
      return `/blog/${key}`;
    }
    return hrefReplace
  }, [locale]);

  const linkChildren = (
    <>
      {t(`lang.${locale}`)}
    </>
  );

  return (
    <div
      className={classNames(
        'lang-box__item',
        active && 'lang-box__item--active',
      )}
    >
      {active ? (
        <span
          className="lang-box__link"
          role="menuitem"
          aria-current="page"
        >
          {linkChildren}
        </span>
      ) : (
        <Link href={newHref} locale={locale}>
          <a
            className="lang-box__link"
            role="menuitem"
            hrefLang={locale}
            onClick={handleClick}
          >
            {linkChildren}
          </a>
        </Link>
      )}
    </div>
  );
};

const checkLangForAnalytics = (val) => {
  let lang = '';
  switch (val) {
    case 'en':
      lang = 'english';
      break;
    case 'es':
      lang = 'spanish';
      break;
    case 'pt':
      lang = 'portuguese';
      break;
    case 'de':
      lang = 'german';
      break;
    case 'fr':
      lang = 'french';
      break;
    case 'ru':
      lang = 'russian';
      break;
    case 'id':
      lang = 'indonesian';
      break;
    case 'it':
      lang = 'italian';
      break;
    case 'tr':
      lang = 'turkish';
      break;
  }
  return lang;
};

LangBox.propTypes = {
  className: PropTypes.string,
};

LangBoxItem.propTypes = {
  href: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  active: PropTypes.bool,
  currentLocale: PropTypes.string,
};

export default LangBox;
